import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { updateIngestAlerts } from '../../../camaraManager/api/camaraManager.api';
import AlertasObra from './AlertasObra';
import { estadoForzado } from '../comboIA/comboIA';
import AddonEntregas from './AddonEntregas';
import AlertasSelector, { TIPOS, EstadoChip } from './AlertasSelector';
import { alertasApi } from './alertasObra.api';
import AlertasClima from '../weather/clima/AlertasClima';

/* Inteligencia › Alertas: arriba cuatro tarjetas lado a lado (🏗️ obra · 📷 cámara · 🚚 entregas · 🌦️ clima) y
   abajo la configuración de la elegida. Las tres configuraciones quedan montadas (se ocultan con
   `hidden`) para no perder cambios sin guardar al cambiar de tarjeta y para que la vuelta del pago
   de entregas (?ae=...) se procese siempre. */

const EJEMPLOS = {
  camara: {
    hora: '09:15',
    texto: '📷 *Cámara sin fotos*\nLa cámara de tu obra no mandó imágenes desde ayer. Revisá que tenga luz y señal.',
  },
  obra: {
    hora: '17:30',
    texto: '🏗️ *Ojo con la obra*\nHoy trabajó menos de la mitad de la gente habitual. Mirá la foto de las 10:00 👇',
  },
  entregas: {
    hora: '10:42',
    texto: '🚚 *Llegó material a la obra*\nEntró un camión a las 10:42. Mirá la foto de antes y después 👇',
    foto: true,
  },
  clima: {
    hora: '17:00',
    texto: '🌧️ *Mañana llueve*\nMañana martes 21 se esperan 8 mm entre las 9 y las 13 h (probabilidad 80 %). Planificá tareas bajo techo.',
  },
};

function PreviewWhatsApp({ tipo }) {
  const e = EJEMPLOS[tipo];
  if (!e) return null;
  const [titulo, ...resto] = e.texto.split('\n');
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-4 pt-3 pb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Así te llega</p>
      </div>
      <div className="mx-3 mb-3 rounded-xl p-3 bg-[#efeae2]">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">L</span>
          <div className="leading-tight">
            <p className="text-xs font-semibold text-gray-800">Lisual</p>
            <p className="text-[10px] text-gray-500">WhatsApp</p>
          </div>
        </div>
        <div className="relative max-w-[95%] bg-white rounded-lg rounded-tl-none shadow-sm px-2.5 pt-2 pb-1">
          {e.foto && (
            <div className="grid grid-cols-2 gap-1 mb-1.5" aria-hidden="true">
              <div className="h-14 rounded bg-gradient-to-br from-gray-200 to-gray-300 flex items-end p-1"><span className="text-[9px] font-semibold text-gray-600 bg-white/80 rounded px-1">Antes</span></div>
              <div className="h-14 rounded bg-gradient-to-br from-amber-100 to-amber-300 flex items-center justify-center relative"><span className="text-2xl">🚚</span><span className="absolute bottom-1 left-1 text-[9px] font-semibold text-gray-600 bg-white/80 rounded px-1">Después</span></div>
            </div>
          )}
          <p className="text-[13px] font-semibold text-gray-900">{titulo.replace(/\*/g, '')}</p>
          <p className="text-[13px] text-gray-800 whitespace-pre-line leading-snug">{resto.join('\n')}</p>
          <p className="text-[10px] text-gray-400 text-right mt-0.5">{e.hora} ✓✓</p>
        </div>
      </div>
      <p className="px-4 pb-3 text-[11px] text-gray-500">Es un ejemplo. Los avisos salen entre las 8 y las 20 h.</p>
    </div>
  );
}

// ---------- panel de la cámara (aviso por cámara sin imágenes) ----------
function PanelCamara({ activa, actualizando, onToggle }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5">
      <button
        type="button"
        role="switch"
        aria-checked={activa}
        disabled={actualizando}
        onClick={onToggle}
        className={`w-full flex items-center justify-between gap-4 rounded-xl border-2 p-4 text-left transition-colors ${
          activa ? 'border-emerald-200 bg-emerald-50/60' : 'border-gray-200 bg-gray-50/60 hover:bg-gray-50'
        } ${actualizando ? 'opacity-60 cursor-wait' : ''}`}
      >
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900">{activa ? 'Te estamos avisando' : 'No te estamos avisando'}</p>
          <p className="text-sm text-gray-600 mt-0.5">
            {activa
              ? 'Si un día la cámara no manda fotos, te llega un WhatsApp.'
              : 'Prendelo y te mandamos un WhatsApp si la cámara se queda sin fotos.'}
          </p>
        </div>
        <span className={`w-14 h-8 flex items-center rounded-full p-1 transition-colors shrink-0 ${activa ? 'bg-blue-600' : 'bg-gray-300'}`}>
          <span className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform ${activa ? 'translate-x-6' : 'translate-x-0'}`} />
        </span>
      </button>
      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-gray-700">
        <li className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2"><span aria-hidden="true">💡</span>Suele ser luz o señal</li>
        <li className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2"><span aria-hidden="true">💬</span>Llega por WhatsApp</li>
        <li className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2"><span aria-hidden="true">🔁</span>Una vez por día, sin repetir</li>
      </ul>
    </div>
  );
}

const tabInicial = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('ae')) return 'entregas'; // vuelta del pago de entregas
    const t = q.get('alerta');
    if (TIPOS.some((x) => x.key === t)) return t;
  } catch (e) { /* sin URL: queda la obra */ }
  return 'obra';
};

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

const Alertas = ({ feedID, ingestAlertsEnabled = false, onIngestAlertsChange, telefonoSugerido }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [elegida, setElegida] = useState(tabInicial);
  // En la preview local no llega onIngestAlertsChange: se refleja el cambio igual.
  const [camaraOn, setCamaraOn] = useState(!!ingestAlertsEnabled);
  const [resumenObra, setResumenObra] = useState(null);
  const [resumenEntregas, setResumenEntregas] = useState(null);

  useEffect(() => { setCamaraOn(!!ingestAlertsEnabled); }, [ingestAlertsEnabled]);

  const handleToggle = async () => {
    const nextValue = !camaraOn;
    setIsUpdating(true);
    try {
      await updateIngestAlerts(feedID, nextValue);
      setCamaraOn(nextValue);
      onIngestAlertsChange && onIngestAlertsChange(nextValue);
      toast.success(nextValue ? 'Aviso por cámara sin imágenes activado.' : 'Aviso por cámara sin imágenes desactivado.');
    } catch (error) {
      toast.error('No se pudo actualizar el aviso de la cámara.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Resumen para las tarjetas (solo lectura; cada panel sigue cargando lo suyo).
  const cargarResumen = useCallback(() => {
    if (!feedID) return;
    alertasApi.listar(feedID).then(setResumenObra).catch(() => setResumenObra({ error: true }));
    if (estadoForzado()) setResumenEntregas(estadoForzado()); // solo local: ?combo=activo|prueba|no
    else alertasApi.comboEstado(feedID).then(setResumenEntregas).catch(() => setResumenEntregas({ error: true }));
  }, [feedID]);
  useEffect(() => { cargarResumen(); }, [cargarResumen]);

  const [climaVisto, setClimaVisto] = useState(elegida === 'clima');
  const elegir = (k) => { setElegida(k); if (k === 'clima') setClimaVisto(true); cargarResumen(); };

  // ---- estados de las tarjetas ----
  const configs = resumenObra?.configs || [];
  const activosObra = configs.filter((c) => c.activo && c.obra?.habilitada);
  const conEntregas = configs.filter((c) => c.activo && c.obra?.habilitada && c.obra?.alertas?.entregas?.activo);
  const climaOn = (c) => Object.values(c.obra?.alertas?.clima || {}).some((x) => x && x.activo);
  const conClima = configs.filter((c) => c.activo && c.obra?.habilitada && climaOn(c));
  const avisosClima = configs[0] ? Object.values(configs[0].obra?.alertas?.clima || {}).filter((x) => x && x.activo).length : 0;

  const estados = {
    camara: camaraOn
      ? { tono: 'on', txt: 'Activada', detalle: 'Por WhatsApp' }
      : { tono: 'off', txt: 'Desactivada' },
    obra: !resumenObra
      ? { tono: 'cargando', txt: 'Cargando…' }
      : resumenObra.error
        ? { tono: 'off', txt: 'Sin datos' }
        : activosObra.length
          ? { tono: 'on', txt: 'Activada', detalle: plural(activosObra.length, 'destinatario', 'destinatarios') }
          : { tono: 'off', txt: 'Desactivada', detalle: configs.length ? 'En pausa' : 'Nadie la recibe' },
    entregas: !resumenEntregas
      ? { tono: 'cargando', txt: 'Cargando…' }
      : resumenEntregas.error
        ? { tono: 'off', txt: 'Sin datos' }
        : resumenEntregas.pagoPendiente
          ? { tono: 'alerta', txt: 'Revisar pago' }
          : resumenEntregas.contratado
            ? { tono: 'on', txt: resumenEntregas.feedIncluido ? 'Activada' : 'Activando', detalle: conEntregas.length ? plural(conEntregas.length, 'destinatario', 'destinatarios') : null }
            : { tono: 'alerta', txt: 'En proceso', detalle: `IA de obra · USD ${resumenEntregas.priceUsd || 20}/mes` },
    clima: !resumenObra
      ? { tono: 'cargando', txt: 'Cargando…' }
      : resumenObra.error
        ? { tono: 'off', txt: 'Sin datos' }
        : conClima.length
          ? { tono: 'on', txt: 'Activada', detalle: `${plural(avisosClima, 'aviso', 'avisos')} · ${plural(conClima.length, 'destinatario', 'destinatarios')}` }
          : { tono: 'nuevo', txt: 'Nuevo', detalle: 'Prendé los que te sirvan' },
  };

  const tipo = TIPOS.find((t) => t.key === elegida) || TIPOS[0];

  return (
    <div className="space-y-4">
      <AlertasSelector elegida={elegida} onElegir={elegir} estados={estados} />

      <div
        role="tabpanel"
        id={`alertas-panel-${tipo.key}`}
        aria-labelledby={`alertas-tab-${tipo.key}`}
        className={`rounded-2xl border border-gray-200 border-t-4 ${tipo.acento} bg-gray-50/70 p-3 sm:p-4`}
      >
        <div className="flex items-center gap-3 mb-3 px-1">
          <span className={`w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0 ${tipo.circulo}`} aria-hidden="true">{tipo.emoji}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">{tipo.titulo}</h3>
              <EstadoChip estado={estados[tipo.key]} />
            </div>
            <p className="text-xs sm:text-sm text-gray-500">{tipo.bajada}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-4 items-start">
          <div className="min-w-0">
            <div className={elegida === 'camara' ? '' : 'hidden'}>
              <PanelCamara activa={camaraOn} actualizando={isUpdating} onToggle={handleToggle} />
            </div>
            <div className={elegida === 'obra' ? '' : 'hidden'}>
              <AlertasObra feedID={feedID} telefonoSugerido={telefonoSugerido} />
            </div>
            <div className={`${elegida === 'entregas' ? '' : 'hidden'} [&>div>div:first-child_h3]:hidden`}>
              {/* El título propio ya está arriba: se oculta el encabezado interno repetido de la tarjeta. */}
              <AddonEntregas feedID={feedID} onIrAObra={() => elegir('obra')} />
            </div>
            <div className={elegida === 'clima' ? '' : 'hidden'}>
              {/* Se monta recién al elegirla: pide la ubicación y el pronóstico de la obra */}
              {(elegida === 'clima' || climaVisto) && <AlertasClima feedID={feedID} telefonoSugerido={telefonoSugerido} />}
            </div>
          </div>
          <div className="lg:sticky lg:top-4">
            <PreviewWhatsApp tipo={elegida} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Alertas;
